import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router';
import { useLocation } from 'react-router-dom';
import { Spinner } from '@vwfs-bronson/bronson-react';
import { Page } from '.';
import { AppWizard } from './components/AppWizard/AppWizard';
import { BaseWrapper } from './components/BaseWrapper';
import { BtoConfigurator } from './components/BtoConfigurator';
import { ErrorModal } from './components/ErrorModal/ErrorModal';
import { InfoModal } from './components/InfoModal/InfoModal';
import { getBrandName } from './config';
import { isPotentialHybridJourneyRoute, routes } from './routing/routes';
import { AuthenticatePage } from './screens/AuthenticatePage/AuthenticatePage';
import { CreditCheckPendingPage } from './screens/CreditCheckPendingPage/CreditCheckPendingPage';
import { CreditCheckRejectionPage } from './screens/CreditCheckRejectionPage/CreditCheckRejectionPage';
import { CreditCheckSuccessPage } from './screens/CreditCheckSuccessPage/CreditCheckSuccessPage';
import { DashboardWrapper } from './screens/Dashboard/DashboardWrapper';
import { DashboardCompare } from './screens/Dashboard/subpages/components/DashboardCompare/DashboardCompare';
import { DashboardSubpageAccepted } from './screens/Dashboard/subpages/DashboardSubpageAccepted/DashboardSubpageAccepted';
import { DashboardSubpageActiveQuotes } from './screens/Dashboard/subpages/DashboardSubpageActiveQuotes/DashboardSubpageActiveQuotes';
import { DashboardDealerDocuments } from './screens/Dashboard/subpages/DashboardSubpageDealerDocuments/DashboardSubpageDealerDocuments';
import {
  DashboardSubpageDocuments,
  DashboardSubpageDocumentsSourceType,
} from './screens/Dashboard/subpages/DashboardSubpageDocuments/DashboardSubpageDocuments';
import { DashboardSubpageHome } from './screens/Dashboard/subpages/DashboardSubpageHome/DashboardSubpageHome';
import { DashboardSubpagePayment } from './screens/Dashboard/subpages/DashboardSubpagePayment/DashboardSubpagePayment';
import { DashboardSubpageQuotes } from './screens/Dashboard/subpages/DashboardSubpageQuotes/DashboardSubpageQuotes';
import { DashboardSubpageRun } from './screens/Dashboard/subpages/DashboardSubpageRun/DashboardSubpageRun';
import { DashboardSubpageStudies } from './screens/Dashboard/subpages/DashboardSubpageStudies/DashboardSubpageStudies';
import { useFetchNotifications } from './screens/Dashboard/useFetchNotifications';
import { ErrorPage } from './screens/ErrorPage/ErrorPage';
import { FormPage } from './screens/FormPage';
import { SelectCarType } from './screens/FormPage/Step1/SelectCarType';
import { SelectCustomerType } from './screens/FormPage/Step2/SelectCustomerType';
import { IdentFailedPage } from './screens/IdentFailedpage/IdentFailedPage';
import { IdentLandingPage } from './screens/IdentLandingPage/IdentLandingPage';
import { IdentStatusPullPage } from './screens/IdentStatusPullPage';
import { IdentSuccessForPAPage } from './screens/IdentSuccessForPAPage/IdentSuccessForPAPage';
import { IdentSuccessForCoApplicantEmailPage } from './screens/IdentSuccessForPAWithCoApplicantPage/IdentSuccessForCoApplicantEmailPage';
import { IdentSuccessForCoApplicantScrivePage } from './screens/IdentSuccessForPAWithCoApplicantPage/IdentSuccessForCoApplicantScrivePage';
import { IdentSuccessForPAWithCoApplicantPage } from './screens/IdentSuccessForPAWithCoApplicantPage/IdentSuccessForPAWithCoApplicantPage';
import { LinkExpiredPage } from './screens/LinkExpiredPage/LinkExpiredPage';
import { NotesPage } from './screens/NotesPage/NotesPage';
import { OutletPage } from './screens/OutletPage/OutletPage';
import { ESignLandingPage } from './screens/Signature/ESignLandingPage';
import { ESignRefusedPage } from './screens/Signature/ESignRefusedPage';
import { ESignSuccessPage } from './screens/Signature/ESignSuccessPage';
import { Analytics, getBrowserResolutionBreakpointString } from './services/analytics';
import { getAnalyticsDefaults } from './services/analytics/analyticsDefaults';
import {
  checkLoginAsFovoriteCompany,
  isValidAuthorizationDataPresent,
  updateRouterNavigationRefForAuth,
} from './services/api/authorization';
import { isNotesEnabled } from './services/common/miscUtils';
import { getCacheableState } from './services/redux';
import { setIsHybridJourney } from './services/redux/features/custom.slice';
import { setupRestore } from './services/restore';
import { useNavigate } from './services/routing/navigate';
import { useValidInitialRoutes } from './services/routing/useValidInitialRoutes';
import { useSncPersistLocation } from './services/snc/useSncPersistLocation';

interface AppProps {
  pages: { hybridJourney: Page[]; dealerJourney: Page[] };
  initialLoginFailed: boolean;
}

export const App: FunctionComponent<AppProps> = (props) => {
  const [loginAsFavouriteDone, setLoginAsFavouriteDone] = useState(false);
  const { t } = useTranslation();
  const { pages, initialLoginFailed } = props;
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const location = useLocation();
  const { pathname } = location;

  useFetchNotifications(pathname);

  useSncPersistLocation();
  const initialRouteValidated = useValidInitialRoutes();

  useEffect(() => {
    setupRestore(getCacheableState);
  }, []);

  useEffect(() => {
    if (!initialRouteValidated) return;
    checkLoginAsFovoriteCompany(setLoginAsFavouriteDone);
  }, [initialRouteValidated]);

  useEffect(() => {
    const isHybridJourney = isPotentialHybridJourneyRoute() && !isValidAuthorizationDataPresent();
    dispatch(setIsHybridJourney(isHybridJourney));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    updateRouterNavigationRefForAuth(navigate);
  }, [navigate]);

  if (initialLoginFailed) {
    navigate.error();
  }

  useEffect(() => {
    document.title = t('landingPage:title');
    Analytics.setDefaults(
      getAnalyticsDefaults(getBrandName(), getBrowserResolutionBreakpointString(window.innerWidth))
    );
  }, [t]);

  if (!initialRouteValidated || !loginAsFavouriteDone) return <Spinner fullPage />;

  return (
    <>
      <ErrorModal />
      <InfoModal />

      <Routes>
        <Route
          path={routes.root}
          element={
            <BaseWrapper pages={pages}>
              <OutletPage />
            </BaseWrapper>
          }
        />
        <Route
          path={routes.error}
          element={
            <BaseWrapper pages={pages}>
              <ErrorPage />
            </BaseWrapper>
          }
        />

        <Route
          path={routes.auth}
          element={
            <BaseWrapper pages={pages}>
              <AuthenticatePage />
            </BaseWrapper>
          }
        />

        <Route
          path={routes.linkexpired}
          element={
            <BaseWrapper pages={pages}>
              <LinkExpiredPage />
            </BaseWrapper>
          }
        />

        <Route
          path={routes.selectCarType}
          element={
            <BaseWrapper pages={pages}>
              <AppWizard />
              <SelectCarType />
            </BaseWrapper>
          }
        />

        <Route
          path={routes.configurator}
          element={
            <BaseWrapper pages={pages}>
              <AppWizard />
              <BtoConfigurator />
            </BaseWrapper>
          }
        />

        <Route
          path={routes.selectCustomerType}
          element={
            <BaseWrapper pages={pages}>
              <AppWizard />
              <SelectCustomerType />
            </BaseWrapper>
          }
        />

        <Route
          path={`${routes.form}/:step/:coContractor?`}
          element={
            <BaseWrapper pages={pages}>
              <AppWizard />
              <FormPage />
            </BaseWrapper>
          }
        />

        <Route
          path={routes.creditCheckSuccess}
          element={
            <BaseWrapper pages={pages}>
              <AppWizard />
              <CreditCheckSuccessPage />
            </BaseWrapper>
          }
        />

        <Route
          path={routes.identLanding}
          element={
            <BaseWrapper pages={pages}>
              <IdentLandingPage />
            </BaseWrapper>
          }
        />

        <Route
          path={routes.identLandingCoApplicant}
          element={
            <BaseWrapper pages={pages}>
              <IdentLandingPage isCoApplicant />
            </BaseWrapper>
          }
        />

        <Route
          path={routes.identSuccess}
          element={
            <BaseWrapper pages={pages}>
              <IdentSuccessForPAPage />
            </BaseWrapper>
          }
        />

        <Route
          path={routes.identSuccessCoApplicant}
          element={
            <BaseWrapper pages={pages}>
              <IdentSuccessForPAWithCoApplicantPage />
            </BaseWrapper>
          }
        />

        <Route
          path={routes.identSuccessCoApplicantEmail}
          element={
            <BaseWrapper pages={pages}>
              <IdentSuccessForCoApplicantEmailPage />
            </BaseWrapper>
          }
        />

        <Route
          path={routes.identSuccessCoApplicantScrive}
          element={
            <BaseWrapper pages={pages}>
              <IdentSuccessForCoApplicantScrivePage />
            </BaseWrapper>
          }
        />

        <Route
          path={routes.identFailed}
          element={
            <BaseWrapper pages={pages}>
              <IdentFailedPage />
            </BaseWrapper>
          }
        />

        <Route
          path={routes.idenStatus}
          element={
            <BaseWrapper pages={pages}>
              <IdentStatusPullPage />
            </BaseWrapper>
          }
        />

        <Route
          path={routes.esignStart}
          element={
            <BaseWrapper pages={pages}>
              <ESignLandingPage />
            </BaseWrapper>
          }
        />

        <Route
          path={routes.esignSuccess}
          element={
            <BaseWrapper pages={pages}>
              <ESignSuccessPage />
            </BaseWrapper>
          }
        />

        <Route
          path={routes.esignRefused}
          element={
            <BaseWrapper pages={pages}>
              <ESignRefusedPage />
            </BaseWrapper>
          }
        />

        <Route
          path={routes.creditCheckPending}
          element={
            <BaseWrapper pages={pages}>
              <AppWizard />
              <CreditCheckPendingPage />
            </BaseWrapper>
          }
        />

        <Route
          path={routes.dashboardHome}
          element={
            <BaseWrapper pages={pages}>
              <DashboardWrapper>
                <DashboardSubpageHome />
              </DashboardWrapper>
            </BaseWrapper>
          }
        />

        <Route
          path={routes.dashboardQuotes}
          element={
            <BaseWrapper pages={pages}>
              <DashboardWrapper>
                <DashboardSubpageQuotes />
              </DashboardWrapper>
            </BaseWrapper>
          }
        />

        <Route
          path={routes.dashboardQuotesActive}
          element={
            <BaseWrapper pages={pages}>
              <DashboardWrapper>
                <DashboardSubpageActiveQuotes />
              </DashboardWrapper>
            </BaseWrapper>
          }
        />

        <Route
          path={`${routes.dashboardQuotesDocuments}`}
          element={
            <BaseWrapper pages={pages}>
              <DashboardWrapper>
                <DashboardSubpageDocuments sourceType={DashboardSubpageDocumentsSourceType.QUOTATION_SUMMARY} />
              </DashboardWrapper>
            </BaseWrapper>
          }
        />

        <Route
          path={routes.dashboardStudies}
          element={
            <BaseWrapper pages={pages}>
              <DashboardWrapper>
                <DashboardSubpageStudies />
              </DashboardWrapper>
            </BaseWrapper>
          }
        />

        <Route
          path={`${routes.dashboardStudiesDocuments}`}
          element={
            <BaseWrapper pages={pages}>
              <DashboardWrapper>
                <DashboardSubpageDocuments sourceType={DashboardSubpageDocumentsSourceType.STUDIES} />
              </DashboardWrapper>
            </BaseWrapper>
          }
        />

        <Route
          path={routes.dashboardAccepted}
          element={
            <BaseWrapper pages={pages}>
              <DashboardWrapper>
                <DashboardSubpageAccepted />
              </DashboardWrapper>
            </BaseWrapper>
          }
        />

        <Route
          path={`${routes.dashboardAcceptedDocuments}`}
          element={
            <BaseWrapper pages={pages}>
              <DashboardWrapper>
                <DashboardSubpageDocuments sourceType={DashboardSubpageDocumentsSourceType.ACCEPTED} />
              </DashboardWrapper>
            </BaseWrapper>
          }
        />

        <Route
          path={routes.dashboardPayment}
          element={
            <BaseWrapper pages={pages}>
              <DashboardWrapper>
                <DashboardSubpagePayment />
              </DashboardWrapper>
            </BaseWrapper>
          }
        />
        <Route
          path={routes.dashboardCompare}
          element={
            <BaseWrapper pages={pages}>
              <DashboardWrapper>
                <DashboardCompare />
              </DashboardWrapper>
            </BaseWrapper>
          }
        />

        <Route
          path={routes.dashboardRun}
          element={
            <BaseWrapper pages={pages}>
              <DashboardWrapper>
                <DashboardSubpageRun />
              </DashboardWrapper>
            </BaseWrapper>
          }
        />

        <Route
          path={routes.dashboardDealerDocuments}
          element={
            <BaseWrapper pages={pages}>
              <DashboardWrapper>
                <DashboardDealerDocuments />
              </DashboardWrapper>
            </BaseWrapper>
          }
        />

        {isNotesEnabled() && (
          <Route
            path={routes.dashboardConversations}
            element={
              <BaseWrapper pages={pages}>
                <DashboardWrapper>
                  <NotesPage />
                </DashboardWrapper>
              </BaseWrapper>
            }
          />
        )}

        <Route
          path={`${routes.dashboardRunDocuments}`}
          element={
            <BaseWrapper pages={pages}>
              <DashboardWrapper>
                <DashboardSubpageDocuments sourceType={DashboardSubpageDocumentsSourceType.RUNNING} />
              </DashboardWrapper>
            </BaseWrapper>
          }
        />

        <Route
          path={routes.creditCheckRejection}
          element={
            <BaseWrapper pages={pages}>
              <AppWizard />
              <CreditCheckRejectionPage />
            </BaseWrapper>
          }
        />

        <Route path="*" element={<Navigate to={routes.error} />} />
      </Routes>
    </>
  );
};
